import { AlertTriangle, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import type { Tone } from '@/lib/charging'
import { useI18n } from '@/lib/i18n'
import { cn, errorText } from '@/lib/utils'
import { Button } from './ui/button'
import { Skeleton } from './ui/card'

export function PageTitle({ title, sub, action }: { title: ReactNode; sub?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{title}</h1>
        {sub && <p className="mt-1 text-sm text-text-2">{sub}</p>}
      </div>
      {action}
    </div>
  )
}

export function EmptyState({ icon, title, body, action }: { icon?: ReactNode; title: ReactNode; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      {icon && <span className="mb-3 grid size-12 place-items-center rounded-2xl bg-surface-3 text-text-2 [&_svg]:size-6">{icon}</span>}
      <p className="font-medium">{title}</p>
      {body && <p className="mt-1 max-w-sm text-sm text-text-2">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n()
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="mb-3 grid size-12 place-items-center rounded-2xl bg-danger-soft text-danger">
        <AlertTriangle className="size-6" />
      </span>
      <p className="font-medium">{t("Couldn't load this")}</p>
      <p className="mt-1 max-w-sm text-sm text-text-2">{errorText(error)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw /> {t('Try again')}
        </Button>
      )}
    </div>
  )
}

export function LoadingBlock({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  )
}

const dot: Record<Tone, string> = { success: 'bg-success', brand: 'bg-brand', info: 'bg-info', warning: 'bg-warning', danger: 'bg-danger', neutral: 'bg-muted' }
const soft: Record<Tone, string> = {
  success: 'bg-success-soft text-success',
  brand: 'bg-brand-soft text-brand-ink',
  info: 'bg-info-soft text-info',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  neutral: 'bg-surface-3 text-text-2',
}

export function StatusPill({ tone, children, className }: { tone: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium', soft[tone], className)}>
      <span className={cn('size-1.5 rounded-full', dot[tone])} />
      {children}
    </span>
  )
}

export function Stat({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <p className="text-xs text-text-2">{label}</p>
      <p className="tabular mt-0.5 text-lg font-semibold">{value}</p>
    </div>
  )
}
